import { useMemo } from "react";
import { parseItemDate } from "../../utils/dateUtils";

const DailySummary = ({
  bottleData, solidFoodData, waterData,
  sleepData, milestoneData,
  diaperData, allergyData,
  breastfeedingData,
}) => {
  const today = new Date();
  const todayString = today.toDateString();

  const todayStats = useMemo(() => {
    const todayYear = today.getFullYear();
    const todayMonth = today.getMonth();
    const todayDay = today.getDate();

    const getTodayData = (data = []) =>
      data.filter((item) => {
        const d = parseItemDate(item);
        return d && d.getFullYear() === todayYear && d.getMonth() === todayMonth && d.getDate() === todayDay;
      });

    const todayBottles = getTodayData(bottleData);
    const todaySolids = getTodayData(solidFoodData);
    const todayWater = getTodayData(waterData);
    const todaySleep = getTodayData(sleepData);
    const todayDiapers = getTodayData(diaperData);
    const todayMilestones = getTodayData(milestoneData);
    const todayBreastfeeding = getTodayData(breastfeedingData || []);

    const totalBottleOz = todayBottles.reduce((s, i) => s + parseFloat(i.amount || 0), 0);
    const totalWaterOz = todayWater.reduce((s, i) => s + parseFloat(i.amount || 0), 0);
    const totalBfMinutes = todayBreastfeeding.reduce((s, i) => s + parseFloat(i.duration || 0), 0);

    const totalSleepMinutes = todaySleep.reduce((total, item) => {
      if (item.startTime && item.endTime) {
        const start = new Date(`2000-01-01T${item.startTime}`);
        const end   = new Date(`2000-01-01T${item.endTime}`);
        const raw   = end - start;
        const diff  = raw > 0 ? raw : raw + 24 * 60 * 60 * 1000;
        return total + Math.floor(diff / 60000);
      }
      return total;
    }, 0);

    const formatDuration = (minutes) => {
      const h = Math.floor(minutes / 60);
      const m = minutes % 60;
      return `${h}h ${m}m`;
    };

    const wetDiapers = todayDiapers.filter((d) => d.type === "wet" || d.type === "both").length;
    const dirtyDiapers = todayDiapers.filter((d) => d.type === "dirty" || d.type === "both").length;

    return {
      bottleFeeds: todayBottles.length,
      bottleOz: totalBottleOz.toFixed(1),
      solidFoods: todaySolids.length,
      waterServings: todayWater.length,
      waterOz: totalWaterOz.toFixed(1),
      sleepSessions: todaySleep.length,
      sleepDuration: formatDuration(totalSleepMinutes),
      diapers: todayDiapers.length,
      wetDiapers,
      dirtyDiapers,
      milestones: todayMilestones.length,
      knownAllergens: (allergyData || []).length,
      breastfeedingSessions: todayBreastfeeding.length,
      breastfeedingMinutes: totalBfMinutes,
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bottleData, solidFoodData, waterData, sleepData, diaperData, milestoneData, allergyData, breastfeedingData, todayString]);

  const StatCard = ({ icon, title, value, subtitle }) => (
    <div className="summary-card">
      <div className="summary-card-icon">{icon}</div>
      <p className="summary-card-label">{title}</p>
      <div className="summary-card-value">{value}</div>
      {subtitle && <p className="summary-card-sub">{subtitle}</p>}
    </div>
  );

  return (
    <div className="card">
      <div className="card-header">
        <h5 className="mb-0">📊 Today's Summary — {today.toLocaleDateString()}</h5>
      </div>
      <div className="card-body">
        <div className="summary-cards-grid">
          <StatCard
            icon="🍼"
            title="Bottle Feeds"
            value={todayStats.bottleFeeds}
            subtitle={`${todayStats.bottleOz} oz total`}
          />
          <StatCard
            icon="😴"
            title="Sleep"
            value={todayStats.sleepDuration}
            subtitle={`${todayStats.sleepSessions} session${todayStats.sleepSessions !== 1 ? "s" : ""}`}
          />
          <StatCard
            icon="🧷"
            title="Nappies"
            value={todayStats.diapers}
            subtitle={`${todayStats.wetDiapers} wet · ${todayStats.dirtyDiapers} dirty`}
          />
          <StatCard
            icon="💧"
            title="Water"
            value={`${todayStats.waterOz} oz`}
            subtitle={`${todayStats.waterServings} serving${todayStats.waterServings !== 1 ? "s" : ""}`}
          />
          <StatCard
            icon="🥘"
            title="Solid Foods"
            value={todayStats.solidFoods}
            subtitle="meals today"
          />
          <StatCard
            icon="🎯"
            title="Milestones"
            value={todayStats.milestones}
            subtitle="logged today"
          />
          {todayStats.breastfeedingSessions > 0 && (
            <StatCard
              icon="🤱"
              title="Breastfeeding"
              value={todayStats.breastfeedingSessions}
              subtitle={`${todayStats.breastfeedingMinutes} min total`}
            />
          )}
          {todayStats.knownAllergens > 0 && (
            <StatCard
              icon="⚠️"
              title="Known Allergens"
              value={todayStats.knownAllergens}
              subtitle="on file"
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default DailySummary;
